export const EMPTY_SNAPSHOT_VALUE = "—";
const DEFAULT_VENDOR = "Let EchoJham Provide";
const BRAND_MAROON = [139, 0, 0];
const HEADER_HEIGHT = 56;

export const PROJECT_STAGE_LABELS = {
  event_preparation: "Event Preparation",
  event_live: "Posted on Live Event",
};

export const formatSnapshotValue = (value) => {
  if (value === null || value === undefined) return EMPTY_SNAPSHOT_VALUE;
  const text = String(value).trim();
  return text ? text : EMPTY_SNAPSHOT_VALUE;
};

// Helvetica/WinAnsi cannot encode ₱. jsPDF then emits ± and extra spacing.
// Use the same ASCII peso prefix as ticket receipt PDFs.
export const formatSnapshotBudget = (value) => {
  const raw = formatSnapshotValue(value);
  if (raw === EMPTY_SNAPSHOT_VALUE) return raw;

  return raw
    .replace(/PHP/gi, "P")
    .replace(/[₱±]/g, "P")
    .replace(/[–—]/g, "-")
    .replace(/P\s+/g, "P")
    .replace(/\s*-\s*/g, " - ")
    .replace(/\s+/g, " ")
    .trim();
};

export const formatProjectStatus = (value) => {
  const normalized = String(value || "approved").trim().toLowerCase().replace(/\s+/g, "_");
  if (!normalized) return "Approved";
  if (normalized === "in_review") return "In Review";
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

export const formatProjectStage = (value) => {
  const normalized = String(value || "").trim().toLowerCase().replace(/\s+/g, "_");
  return PROJECT_STAGE_LABELS[normalized] || formatSnapshotValue(value);
};

export const formatProjectDate = (value) => {
  if (value === null || value === undefined || value === "") return EMPTY_SNAPSHOT_VALUE;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return EMPTY_SNAPSHOT_VALUE;
  return parsed.toLocaleDateString("en-PH", {
    timeZone: "Asia/Manila",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const isConcertProject = (project) => String(project?.booking_type || "").toLowerCase() !== "corporate";

export const getProjectSnapshotFilename = (project) => {
  const code = String(project?.request_code || "").trim();
  if (code) return `${code}.pdf`;
  const id = String(project?.id || "").trim();
  return `project-${id || "export"}.pdf`;
};

export const attachmentNames = (attachments) => {
  if (!Array.isArray(attachments) || !attachments.length) return [];

  return attachments
    .map((item, index) => {
      if (item == null) return null;
      if (typeof item === "string") return item.trim() || null;
      const name = String(item?.name || item?.path || "").trim();
      return name || `Attachment ${index + 1}`;
    })
    .filter(Boolean);
};

const vendorValue = (value) => {
  const text = String(value || "").trim();
  return text || DEFAULT_VENDOR;
};

export const buildProjectSnapshot = (project = {}) => {
  const concert = isConcertProject(project);
  const vendors = project.concert_vendor_choices || {};
  const eventName = formatSnapshotValue(project.event_name);

  const client = [
    { label: "Name", value: formatSnapshotValue(project.requester_name) },
    { label: "Email", value: formatSnapshotValue(project.requester_email) },
    { label: "Phone", value: formatSnapshotValue(project.requester_phone) },
    { label: "Company", value: formatSnapshotValue(project.company_name) },
  ];

  const event = [
    { label: "Booking Type", value: formatSnapshotValue(project.booking_type) },
    { label: "Theme", value: formatSnapshotValue(project.theme) },
    { label: "Venue", value: formatSnapshotValue(project.venue) },
    { label: "Date", value: formatProjectDate(project.event_date) },
    { label: "Budget", value: formatSnapshotBudget(project.budget_range) },
    { label: "Attendee Count", value: formatSnapshotValue(project.attendee_count) },
  ];

  const requirements = concert
    ? [
        { label: "Technical Rider", value: vendorValue(vendors.technical_rider) },
        { label: "Musical Director", value: vendorValue(vendors.musical_director) },
        { label: "Food", value: vendorValue(vendors.food) },
        { label: "Additional Requirements", value: formatSnapshotValue(project.details) },
      ]
    : [
        { label: "Specific Needs", value: formatSnapshotValue(project.specific_needs) },
        { label: "Event Details", value: formatSnapshotValue(project.details) },
      ];

  return {
    filename: getProjectSnapshotFilename(project),
    isConcert: concert,
    requestCode: formatSnapshotValue(project.request_code),
    eventName: eventName === EMPTY_SNAPSHOT_VALUE ? "Untitled Event" : eventName,
    status: formatProjectStatus(project.status),
    stage: formatProjectStage(project.project_stage),
    client,
    event,
    requirements,
    contractUrl: formatSnapshotValue(project.signed_contract_url),
    attachments: attachmentNames(project.attachments),
  };
};

const createPdfDocument = () => {
  const { jsPDF } = require("jspdf");
  return new jsPDF({ unit: "pt", format: "a4" });
};

export const buildProjectSnapshotPdf = (project) => {
  const snapshot = buildProjectSnapshot(project);
  const doc = createPdfDocument();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 48;
  const contentWidth = pageWidth - margin * 2;
  const bottom = pageHeight - 40;
  let y = 0;

  const drawChrome = () => {
    doc.setFillColor(...BRAND_MAROON);
    doc.rect(0, 0, pageWidth, HEADER_HEIGHT, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(255, 255, 255);
    doc.text("ECHO JHAM", margin, 26);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text("Project Brief", margin, 42);
    doc.setFont("helvetica", "bold");
    doc.text(String(snapshot.requestCode), pageWidth - margin, 34, { align: "right" });
  };

  const ensureSpace = (needed) => {
    if (y + needed <= bottom) return;
    doc.addPage();
    drawChrome();
    y = HEADER_HEIGHT + 28;
  };

  const writeWrapped = (text, { fontSize = 10, font = "normal", color = [20, 20, 20], lineHeight = 13 } = {}) => {
    doc.setFont("helvetica", font);
    doc.setFontSize(fontSize);
    doc.setTextColor(...color);
    const lines = doc.splitTextToSize(String(text || ""), contentWidth);
    lines.forEach((line) => {
      ensureSpace(lineHeight);
      doc.text(line, margin, y);
      y += lineHeight;
    });
  };

  const writeSectionTitle = (title) => {
    ensureSpace(36);
    y += 10;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...BRAND_MAROON);
    doc.text(String(title).toUpperCase(), margin, y);
    y += 6;
    doc.setDrawColor(...BRAND_MAROON);
    doc.setLineWidth(1);
    doc.line(margin, y, pageWidth - margin, y);
    y += 16;
  };

  const writeField = (label, value) => {
    ensureSpace(28);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(110, 110, 110);
    doc.text(String(label).toUpperCase(), margin, y);
    y += 13;
    writeWrapped(value, { fontSize: 10, font: "normal", color: [20, 20, 20], lineHeight: 13 });
    y += 8;
  };

  drawChrome();
  y = HEADER_HEIGHT + 32;
  writeWrapped(snapshot.eventName, { fontSize: 18, font: "bold", color: [20, 20, 20], lineHeight: 22 });
  y += 6;
  writeWrapped(`${snapshot.status}  ·  ${snapshot.stage}`, {
    fontSize: 10,
    font: "normal",
    color: [90, 90, 90],
    lineHeight: 14,
  });

  writeSectionTitle("Client");
  snapshot.client.forEach((field) => writeField(field.label, field.value));

  writeSectionTitle("Event");
  snapshot.event.forEach((field) => writeField(field.label, field.value));

  writeSectionTitle(snapshot.isConcert ? "Vendors & Requirements" : "Requirements");
  snapshot.requirements.forEach((field) => writeField(field.label, field.value));

  if (snapshot.contractUrl !== EMPTY_SNAPSHOT_VALUE) {
    writeSectionTitle("Signed Contract");
    writeField("Contract URL", snapshot.contractUrl);
  }

  if (snapshot.attachments.length) {
    writeSectionTitle("Attachments");
    snapshot.attachments.forEach((name, index) => writeField(`Attachment ${index + 1}`, name));
  }

  return doc;
};

export const downloadProjectSnapshotPdf = (project) => {
  if (!project) return null;
  const snapshot = buildProjectSnapshot(project);
  const doc = buildProjectSnapshotPdf(project);
  doc.save(snapshot.filename);
  return snapshot.filename;
};
